import { useMemo } from 'react';
import { Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import type { StatusSummaryData } from '@/api/media/live-live-streams-types';
import { streamStatusTone, type StreamStatusTone } from '../live-stream-status';
import { CompactStat, CompactStatGroup } from '@/pages/services/components/CompactStat';
import styles from './DashboardPage.module.css';

// Status ordering matches legacy dashboard STATUS_ORDER.
const STATUS_ORDER = ['streaming', 'to be started', 'ended', 'forbidden', 'interrupted'];

const STATUS_LABELS: Record<string, string> = {
  'streaming': 'Streaming',
  'to be started': 'To Be Started',
  'ended': 'Ended',
  'forbidden': 'Forbidden',
  'interrupted': 'Interrupted',
};

// Dot color map derived from the streamStatusTone intent tokens.
// Rule: --vn-{intent}-solid for active intents; muted/neutral → --vn-fg-muted.
const TONE_DOT: Record<StreamStatusTone, string> = {
  success: 'var(--vn-success-solid)',
  danger: 'var(--vn-danger-solid)',
  warning: 'var(--vn-warning-solid)',
  info: 'var(--vn-info-solid)',
  neutral: 'var(--vn-fg-muted)',
  muted: 'var(--vn-fg-muted)',
};

// Derive per-status dot color from streamStatusTone so it stays consistent with StatusPill.
const STATUS_DOT: Record<string, string> = Object.fromEntries(
  STATUS_ORDER.map((s) => [s, TONE_DOT[streamStatusTone(s)]]),
);

interface StatusStatCardsProps {
  summary: StatusSummaryData | undefined;
  isLoading: boolean;
}

export function StatusStatCards({ summary, isLoading }: StatusStatCardsProps) {
  const stats = useMemo(
    () =>
      STATUS_ORDER.map((key) => ({
        key,
        label: STATUS_LABELS[key] ?? key,
        dotColor: STATUS_DOT[key] ?? 'var(--vn-fg-muted)',
        count: summary?.counts?.[key] ?? 0,
      })),
    [summary],
  );

  if (isLoading) {
    return (
      <div className={styles.skeletonRow}>
        {STATUS_ORDER.map((k) => (
          <Skeleton key={k} variant="rect" height={72} />
        ))}
      </div>
    );
  }

  return (
    <Card flat>
      <CardBody>
        <CompactStatGroup columns={5}>
          {stats.map(({ key, label, dotColor, count }) => (
            <CompactStat
              key={key}
              label={label}
              value={count.toLocaleString()}
              dotColor={dotColor}
            />
          ))}
        </CompactStatGroup>
      </CardBody>
    </Card>
  );
}
